// Narrow, single-purpose Shopify-OAuth-round-trip project persistence.
// See docs/product/bugs/connect-cta-new-tab-project-leak/trd.md Section
// 2.3/2.4 and Section 3, R7.
//
// Replaces the general-purpose writePersisted/readPersisted that used to
// live in currentProjectSlice.js (removed there per this TRD's Section
// 3, R8) -- that mechanism hydrated Redux's currentProject from
// sessionStorage on every store init and wrote through on every set/clear
// dispatch, which is exactly what let an unrelated new browser tab pick up
// a stale project ID (this bug). This module is now the ONLY remaining
// storage-backed path for currentProject, scoped to the one case that
// genuinely still needs a storage signal: the same-tab full-page Shopify
// OAuth redirect, whose return leg cannot always carry a project ID in
// its path (cq-api's cookie-missing callback branch -- TRD Section 2.4).
//
// Called from exactly two places: ShopifyOnboardingAuth.jsx's login()
// (saveForShopifyRedirect, immediately before the same-tab redirect away)
// and LegacyProjectRedirect.jsx's /grant-access alias
// (consumeForShopifyReturn, on mount) -- plus NavBar.js's handleLogout,
// which calls consumeForShopifyReturn() purely to clear any lingering key
// left behind by an abandoned mid-flow Shopify grant (TRD Section 3, R9).
export const CURRENT_PROJECT_STORAGE_KEY = 'cm_current_project';

export const saveForShopifyRedirect = (project) => {
  try {
    window.sessionStorage.setItem(CURRENT_PROJECT_STORAGE_KEY, JSON.stringify(project));
  } catch (err) {
    // Best-effort persistence only -- same fail-open-but-harmless behavior
    // as the mechanism this replaces (currentProjectSlice.js's old
    // writePersisted). The in-flight Shopify redirect is not blocked by a
    // sessionStorage write failure (e.g. some private browsing modes).
  }
};

// Read-once semantics: always removes the key regardless of parse success,
// so nothing lingers for an unrelated tab to inherit even if this is
// somehow hit twice (TRD AC-16).
export const consumeForShopifyReturn = () => {
  let result = null;
  try {
    const raw = window.sessionStorage.getItem(CURRENT_PROJECT_STORAGE_KEY);
    result = raw ? JSON.parse(raw) : null;
  } catch (err) {
    // Malformed JSON, or sessionStorage unavailable -- fail closed to "no
    // in-flight Shopify return" rather than throwing.
    result = null;
  } finally {
    try {
      window.sessionStorage.removeItem(CURRENT_PROJECT_STORAGE_KEY);
    } catch (err) {
      // Best-effort removal only.
    }
  }
  return result;
};
